import * as React from 'react'
import dynamic from 'next/dynamic'

import { i18n, useSiteConfig } from '@thg-commerce/enterprise-core'
import {
  styled,
  useTheme,
} from '@thg-commerce/enterprise-pages/src/Product/theme'
import { Separator } from '@thg-commerce/gravity-elements'
import { spacing } from '@thg-commerce/gravity-theme'

const BnplOptionsIcon = dynamic(
  () => import('@thg-commerce/gravity-icons/src/components/BnplOptions'),
  { ssr: true },
)

import { useProductPage } from '../../../context/ProductPageContext'
import { BuyNowPayLaterModalProps } from '../BuyNowPayLaterModal/BuyNowPayLaterModal'
import { BuyNowPayLaterPaymentOptions } from '../BuyNowPayLaterPaymentOptions'
import { BuyNowPayLaterTrigger } from '../BuyNowPayLaterTrigger'
import {
  GenericText,
  Title,
  ViewOptionsContainer,
  ViewOptionsIconContainer,
} from '../styles'

const BuyNowPayLaterModal = dynamic<BuyNowPayLaterModalProps>(() =>
  import('../BuyNowPayLaterModal').then((mod) => mod.BuyNowPayLaterModal),
)

const OptionsContainer = styled.div`
  margin-top: ${spacing(2)};
  padding: ${spacing(2)};
  border-radius: ${(props) =>
    props.theme.pageTheme.buyNowPayLater?.borderRadius ?? `8px`};
  background-color: ${(props) =>
    props.theme.pageTheme.buyNowPayLater?.backgroundColor ??
    props.theme.colors.palette.brand.lightest};

  li {
    align-items: center;
    margin-top: 0;
    &:not(:last-child) {
      margin-bottom: ${spacing(1)};
    }
  }
  li > p {
    margin-top: 0;
  }
`

export const BuyNowPayLaterContainer = () => {
  const { selectedVariant } = useProductPage()
  const theme = useTheme()
  const { buyNowPayLaterProvidersEnabled } = useSiteConfig()

  const [buyNowPayLaterModalOpen, setBuyNowPayLaterModalOpen] = React.useState(
    false,
  )

  const i18nTextValues = {
    bnplTitleText: i18n('product.buynowpaylater.title.text'),
    bnplGenericText: i18n('product.buynowpaylater.generic.text'),
    bnplViewoptionsText: i18n('product.buynowpaylater.viewoptions.text'),
    bnplCloseText: i18n('general.modal.close.button.arialabel'),
  }

  if (
    !buyNowPayLaterProvidersEnabled ||
    !selectedVariant?.buyNowPayLaterProviders
  ) {
    return null
  }

  if (selectedVariant?.buyNowPayLaterProviders?.length >= 3) {
    return (
      <React.Fragment>
        <div>
          <OptionsContainer>
            {theme.pageTheme.buyNowPayLater?.title?.show ?? true ? (
              <Title>{i18nTextValues.bnplTitleText}</Title>
            ) : null}
            <GenericText>{i18nTextValues.bnplGenericText}</GenericText>
            <ViewOptionsContainer>
              <ViewOptionsIconContainer>
                <BnplOptionsIcon
                  aria-label={i18nTextValues.bnplViewoptionsText}
                />
              </ViewOptionsIconContainer>
              <BuyNowPayLaterTrigger
                i18nText={{ triggerText: i18nTextValues.bnplViewoptionsText }}
                onClick={() => {
                  setBuyNowPayLaterModalOpen(true)
                }}
              />
            </ViewOptionsContainer>
            {buyNowPayLaterModalOpen && (
              <BuyNowPayLaterModal
                i18nText={{
                  closeI18nText: {
                    closeAriaLabel: i18nTextValues.bnplCloseText,
                    closeLabel: i18nTextValues.bnplCloseText,
                  },
                  title: i18nTextValues.bnplTitleText,
                }}
                onClose={() => {
                  setBuyNowPayLaterModalOpen(false)
                }}
                providersContent={selectedVariant.buyNowPayLaterProviders}
                content={i18nTextValues.bnplGenericText}
              />
            )}
          </OptionsContainer>
        </div>
        <Separator
          withSpacing
          separatorMargin={{ top: 2, right: 0, bottom: 2, left: 0 }}
        />
      </React.Fragment>
    )
  }

  if (selectedVariant?.buyNowPayLaterProviders?.length > 0) {
    return (
      <React.Fragment>
        <OptionsContainer>
          {theme.pageTheme.buyNowPayLater?.title?.show ?? true ? (
            <Title>{i18nTextValues.bnplTitleText}</Title>
          ) : null}
          <BuyNowPayLaterPaymentOptions
            providersContent={selectedVariant.buyNowPayLaterProviders}
          />
        </OptionsContainer>
        <Separator
          withSpacing
          separatorMargin={{ top: 2, right: 0, bottom: 2, left: 0 }}
        />
      </React.Fragment>
    )
  }

  return null
}
